<template>
    <div class="spotlight-widget spotlight-weather-widget" :class="weatherWidgetClass">
        <div v-if="!isPending && effectType" class="sw-fx" :class="'sw-fx--' + effectType">
            <span v-for="n in effectCount" :key="n"></span>
        </div>

        <template v-if="isPending">
            <div class="spotlight-widget-icon spotlight-weather-widget-icon spotlight-weather-icon--loading">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z"/></svg>
            </div>
            <div class="spotlight-widget-content">
                <div class="spotlight-weather-title">{{ displayTitle }}</div>
                <div class="spotlight-weather-pending">{{ $t('common.spotlight.weather.pending') }}</div>
            </div>
        </template>
        <template v-else>
            <div class="spotlight-weather-icon-area">
                <div class="spotlight-weather-condition-icon" v-html="conditionSvg"></div>
            </div>
            <div class="spotlight-weather-body">
                <div class="spotlight-weather-top">
                    <div class="spotlight-weather-temp-block">
                        <span class="spotlight-weather-temp">{{ Math.round(meta.temperature) }}°</span>
                        <span class="spotlight-weather-feels">{{ $t('common.spotlight.weather.feelsLike') }} {{ Math.round(meta.feels_like) }}°</span>
                    </div>
                    <div class="spotlight-weather-info">
                        <div class="spotlight-weather-title">{{ displayTitle }}</div>
                        <div class="spotlight-weather-condition">{{ meta.condition_label }}</div>
                    </div>
                </div>
                <div class="spotlight-weather-details">
                    <template v-if="highLow">
                        <div class="spotlight-weather-detail">
                            <span>↑{{ highLow.high }}° ↓{{ highLow.low }}°</span>
                        </div>
                        <div class="spotlight-weather-detail-sep"></div>
                    </template>
                    <div class="spotlight-weather-detail">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>
                        <span>%{{ meta.humidity }}</span>
                    </div>
                    <div class="spotlight-weather-detail-sep"></div>
                    <div class="spotlight-weather-detail">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/></svg>
                        <span>{{ meta.wind_speed }} km/h</span>
                    </div>
                    <template v-if="meta.uv_index != null">
                        <div class="spotlight-weather-detail-sep"></div>
                        <div class="spotlight-weather-detail">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                            <span>UV {{ meta.uv_index }}</span>
                        </div>
                    </template>
                    <template v-if="precipitationProbability">
                        <div class="spotlight-weather-detail-sep"></div>
                        <div class="spotlight-weather-detail">
                            <span>%{{ precipitationProbability }} 🌧</span>
                        </div>
                    </template>
                    <template v-if="sunTimes">
                        <div class="spotlight-weather-detail-sep"></div>
                        <div class="spotlight-weather-detail">
                            <span>☀ {{ sunTimes.rise }}  🌙 {{ sunTimes.set }}</span>
                        </div>
                    </template>
                </div>

                <div v-if="hourlyItems.length" class="spotlight-weather-hourly">
                    <div v-for="h in hourlyItems" :key="h.time" class="spotlight-weather-hour">
                        <span class="spotlight-weather-hour-time">{{ h.timeLabel }}</span>
                        <span class="spotlight-weather-hour-icon" v-html="h.svg"></span>
                        <span class="spotlight-weather-hour-temp">{{ h.temp }}°</span>
                    </div>
                </div>

                <div v-if="tomorrow" class="spotlight-weather-tomorrow">
                    <span class="spotlight-weather-tomorrow-label">{{ $t('common.spotlight.weather.tomorrow') }}</span>
                    <span class="spotlight-weather-tomorrow-icon" v-html="tomorrowSvg"></span>
                    <span class="spotlight-weather-tomorrow-temps">{{ Math.round(tomorrow.temp_min) }}° / {{ Math.round(tomorrow.temp_max) }}°</span>
                    <span class="spotlight-weather-tomorrow-condition">{{ tomorrow.condition?.label }}</span>
                    <span v-if="tomorrow.precipitation_probability" class="spotlight-weather-tomorrow-precip">%{{ tomorrow.precipitation_probability }} 🌧</span>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
const CONDITION_SVGS = {
    clear: '<svg viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="14" fill="currentColor" opacity="0.9"/><g stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="32" y1="4" x2="32" y2="12"/><line x1="32" y1="52" x2="32" y2="60"/><line x1="4" y1="32" x2="12" y2="32"/><line x1="52" y1="32" x2="60" y2="32"/><line x1="12.2" y1="12.2" x2="17.9" y2="17.9"/><line x1="46.1" y1="46.1" x2="51.8" y2="51.8"/><line x1="12.2" y1="51.8" x2="17.9" y2="46.1"/><line x1="46.1" y1="17.9" x2="51.8" y2="12.2"/></g></svg>',
    mainly_clear: '<svg viewBox="0 0 64 64" fill="none"><circle cx="26" cy="28" r="12" fill="currentColor" opacity="0.85"/><g stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="26" y1="6" x2="26" y2="12"/><line x1="26" y1="44" x2="26" y2="50"/><line x1="6" y1="28" x2="12" y2="28"/><line x1="40" y1="28" x2="46" y2="28"/><line x1="12.1" y1="14.1" x2="16.3" y2="18.3"/><line x1="35.7" y1="37.7" x2="39.9" y2="41.9"/><line x1="12.1" y1="41.9" x2="16.3" y2="37.7"/><line x1="35.7" y1="18.3" x2="39.9" y2="14.1"/></g><path d="M42 36a8 8 0 0 1 0 16H22a6 6 0 0 1 0-12h1a8 8 0 0 1 15.6-2.4A8 8 0 0 1 42 36z" fill="currentColor" opacity="0.35"/></svg>',
    partly_cloudy: '<svg viewBox="0 0 64 64" fill="none"><circle cx="24" cy="24" r="11" fill="currentColor" opacity="0.7"/><g stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity="0.5"><line x1="24" y1="4" x2="24" y2="10"/><line x1="24" y1="38" x2="24" y2="44"/><line x1="4" y1="24" x2="10" y2="24"/><line x1="38" y1="24" x2="44" y2="24"/></g><path d="M46 38a9 9 0 0 1 0 18H18a7 7 0 0 1 0-14h1a9 9 0 0 1 17.4-2.8A9 9 0 0 1 46 38z" fill="currentColor" opacity="0.5"/></svg>',
    overcast: '<svg viewBox="0 0 64 64" fill="none"><path d="M48 34a10 10 0 0 1 0 20H16a8 8 0 0 1 0-16h1a10 10 0 0 1 19.2-3.2A10 10 0 0 1 48 34z" fill="currentColor" opacity="0.5"/><path d="M40 24a7 7 0 0 1 0 14H22a5 5 0 0 1 0-10h.5a7 7 0 0 1 13.5-2.2A7 7 0 0 1 40 24z" fill="currentColor" opacity="0.3"/></svg>',
    fog: '<svg viewBox="0 0 64 64" fill="none"><g stroke="currentColor" stroke-width="3" stroke-linecap="round" opacity="0.5"><line x1="10" y1="22" x2="54" y2="22"/><line x1="14" y1="32" x2="50" y2="32"/><line x1="10" y1="42" x2="54" y2="42"/><line x1="18" y1="52" x2="46" y2="52"/></g></svg>',
    drizzle: '<svg viewBox="0 0 64 64" fill="none"><path d="M46 26a9 9 0 0 1 0 18H18a7 7 0 0 1 0-14h1a9 9 0 0 1 17.4-2.8A9 9 0 0 1 46 26z" fill="currentColor" opacity="0.45"/><g stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity="0.5"><line x1="22" y1="48" x2="20" y2="54"/><line x1="32" y1="48" x2="30" y2="54"/><line x1="42" y1="48" x2="40" y2="54"/></g></svg>',
    freezing_drizzle: '<svg viewBox="0 0 64 64" fill="none"><path d="M46 26a9 9 0 0 1 0 18H18a7 7 0 0 1 0-14h1a9 9 0 0 1 17.4-2.8A9 9 0 0 1 46 26z" fill="currentColor" opacity="0.45"/><g stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity="0.5"><line x1="22" y1="48" x2="20" y2="54"/><line x1="32" y1="48" x2="30" y2="54"/><line x1="42" y1="48" x2="40" y2="54"/></g><circle cx="27" cy="51" r="1.5" fill="currentColor" opacity="0.6"/></svg>',
    rain: '<svg viewBox="0 0 64 64" fill="none"><path d="M46 22a9 9 0 0 1 0 18H18a7 7 0 0 1 0-14h1a9 9 0 0 1 17.4-2.8A9 9 0 0 1 46 22z" fill="currentColor" opacity="0.5"/><g stroke="currentColor" stroke-width="2.5" stroke-linecap="round" opacity="0.6"><line x1="20" y1="44" x2="16" y2="54"/><line x1="30" y1="44" x2="26" y2="54"/><line x1="40" y1="44" x2="36" y2="54"/><line x1="48" y1="44" x2="44" y2="54"/></g></svg>',
    freezing_rain: '<svg viewBox="0 0 64 64" fill="none"><path d="M46 22a9 9 0 0 1 0 18H18a7 7 0 0 1 0-14h1a9 9 0 0 1 17.4-2.8A9 9 0 0 1 46 22z" fill="currentColor" opacity="0.5"/><g stroke="currentColor" stroke-width="2.5" stroke-linecap="round" opacity="0.6"><line x1="20" y1="44" x2="16" y2="54"/><line x1="30" y1="44" x2="26" y2="54"/><line x1="40" y1="44" x2="36" y2="54"/></g><circle cx="44" cy="50" r="2" fill="currentColor" opacity="0.5"/></svg>',
    snow: '<svg viewBox="0 0 64 64" fill="none"><path d="M46 22a9 9 0 0 1 0 18H18a7 7 0 0 1 0-14h1a9 9 0 0 1 17.4-2.8A9 9 0 0 1 46 22z" fill="currentColor" opacity="0.45"/><g fill="currentColor" opacity="0.6"><circle cx="20" cy="48" r="2.5"/><circle cx="32" cy="46" r="2.5"/><circle cx="44" cy="49" r="2.5"/><circle cx="26" cy="55" r="2"/><circle cx="38" cy="56" r="2"/></g></svg>',
    snow_grains: '<svg viewBox="0 0 64 64" fill="none"><path d="M46 22a9 9 0 0 1 0 18H18a7 7 0 0 1 0-14h1a9 9 0 0 1 17.4-2.8A9 9 0 0 1 46 22z" fill="currentColor" opacity="0.45"/><g fill="currentColor" opacity="0.5"><circle cx="20" cy="48" r="1.5"/><circle cx="28" cy="46" r="1.5"/><circle cx="36" cy="49" r="1.5"/><circle cx="44" cy="47" r="1.5"/><circle cx="24" cy="54" r="1.5"/><circle cx="32" cy="55" r="1.5"/><circle cx="40" cy="53" r="1.5"/></g></svg>',
    rain_shower: '<svg viewBox="0 0 64 64" fill="none"><path d="M46 20a9 9 0 0 1 0 18H18a7 7 0 0 1 0-14h1a9 9 0 0 1 17.4-2.8A9 9 0 0 1 46 20z" fill="currentColor" opacity="0.55"/><g stroke="currentColor" stroke-width="2.5" stroke-linecap="round" opacity="0.65"><line x1="18" y1="42" x2="14" y2="52"/><line x1="26" y1="44" x2="22" y2="56"/><line x1="34" y1="42" x2="30" y2="54"/><line x1="42" y1="44" x2="38" y2="56"/><line x1="50" y1="42" x2="46" y2="52"/></g></svg>',
    snow_shower: '<svg viewBox="0 0 64 64" fill="none"><path d="M46 20a9 9 0 0 1 0 18H18a7 7 0 0 1 0-14h1a9 9 0 0 1 17.4-2.8A9 9 0 0 1 46 20z" fill="currentColor" opacity="0.5"/><g fill="currentColor" opacity="0.6"><circle cx="18" cy="46" r="2.5"/><circle cx="28" cy="44" r="2.5"/><circle cx="38" cy="47" r="2.5"/><circle cx="48" cy="45" r="2.5"/><circle cx="22" cy="54" r="2"/><circle cx="33" cy="55" r="2"/><circle cx="44" cy="53" r="2"/></g></svg>',
    thunderstorm: '<svg viewBox="0 0 64 64" fill="none"><path d="M46 18a9 9 0 0 1 0 18H18a7 7 0 0 1 0-14h1a9 9 0 0 1 17.4-2.8A9 9 0 0 1 46 18z" fill="currentColor" opacity="0.55"/><polygon points="30,38 24,50 32,50 28,62 40,46 32,46 36,38" fill="currentColor" opacity="0.85"/></svg>',
    night_clear: '<svg viewBox="0 0 64 64" fill="none"><path d="M38 12a22 22 0 1 0 14 38A22 22 0 0 1 38 12z" fill="currentColor" opacity="0.85"/><circle cx="48" cy="14" r="1.5" fill="currentColor" opacity="0.4"/><circle cx="54" cy="24" r="1" fill="currentColor" opacity="0.3"/><circle cx="44" cy="8" r="1" fill="currentColor" opacity="0.35"/></svg>',
}

const CONDITION_CLASSES = {
    clear: 'clear', mainly_clear: 'clear',
    partly_cloudy: 'cloudy', overcast: 'overcast', fog: 'fog',
    drizzle: 'rain', freezing_drizzle: 'rain', rain: 'rain', freezing_rain: 'rain', rain_shower: 'rain',
    snow: 'snow', snow_grains: 'snow', snow_shower: 'snow',
    thunderstorm: 'storm',
    night_clear: 'night',
}

const EFFECT_MAP = {
    clear: 'sunny', mainly_clear: 'sunny',
    partly_cloudy: 'cloudy', overcast: 'overcast',
    drizzle: 'drizzle', freezing_drizzle: 'drizzle',
    rain: 'rain', freezing_rain: 'rain', rain_shower: 'rain',
    snow: 'snow', snow_grains: 'snow', snow_shower: 'snow',
    thunderstorm: 'storm', fog: 'fog',
    night_clear: 'night',
}

const EFFECT_COUNTS = {
    sunny: 3,
    cloudy: 3,
    overcast: 3,
    drizzle: 6,
    rain: 10,
    snow: 8,
    storm: 12,
    fog: 4,
    night: 5,
}

function titleCase(str) {
    if (!str) return ''
    return str.replace(/\p{L}+/gu, w => w.charAt(0).toLocaleUpperCase('tr') + w.slice(1).toLocaleLowerCase('tr'))
}

export default {
    name: 'WeatherWidget',
    props: {
        item: { type: Object, required: true }
    },
    computed: {
        meta() {
            return this.item.meta || {}
        },
        isPending() {
            return !this.meta.temperature && this.meta.temperature !== 0
        },
        displayTitle() {
            return titleCase(this.item.title)
        },
        isNight() {
            const h = new Date().getHours()
            return h >= 20 || h < 6
        },
        conditionIcon() {
            const icon = this.meta.condition_icon || 'partly_cloudy'
            if (this.isNight && (icon === 'clear' || icon === 'mainly_clear')) return 'night_clear'
            return icon
        },
        conditionSvg() {
            return CONDITION_SVGS[this.conditionIcon] || CONDITION_SVGS.partly_cloudy
        },
        conditionClass() {
            return CONDITION_CLASSES[this.conditionIcon] || 'cloudy'
        },
        weatherWidgetClass() {
            if (this.isPending) return 'spotlight-weather--pending'
            return 'spotlight-weather--' + this.conditionClass
        },
        effectType() {
            return EFFECT_MAP[this.conditionIcon] || null
        },
        effectCount() {
            return EFFECT_COUNTS[this.effectType] || 0
        },
        observedTime() {
            if (!this.meta.observed_at) return ''
            const m = this.$moment(this.meta.observed_at)
            if (!m.isValid()) return ''
            return m.format('HH:mm')
        },
        windDirection() {
            const deg = this.meta.wind_direction
            if (deg == null) return ''
            const dirs = ['K', 'KD', 'D', 'GD', 'G', 'GB', 'B', 'KB']
            return dirs[Math.round(deg / 45) % 8]
        },
        today() {
            return this.meta.today || null
        },
        tomorrow() {
            return this.meta.tomorrow || null
        },
        tomorrowSvg() {
            if (!this.tomorrow?.condition?.icon) return ''
            return CONDITION_SVGS[this.tomorrow.condition.icon] || CONDITION_SVGS.partly_cloudy
        },
        hourlyItems() {
            if (!Array.isArray(this.meta.hourly)) return []
            return this.meta.hourly.map(h => ({
                time: h.time,
                timeLabel: this.$moment(h.time).format('HH:mm'),
                temp: Math.round(h.temperature),
                svg: CONDITION_SVGS[h.icon] || CONDITION_SVGS.partly_cloudy,
            }))
        },
        highLow() {
            if (!this.today) return null
            return { high: Math.round(this.today.temp_max), low: Math.round(this.today.temp_min) }
        },
        precipitationProbability() {
            return this.today?.precipitation_probability || null
        },
        sunTimes() {
            if (!this.today?.sunrise) return null
            return { rise: this.today.sunrise, set: this.today.sunset }
        }
    }
}
</script>

<style>
.spotlight-weather-widget {
    display: flex;
    align-items: center;
    gap: 14px;
    border-radius: 12px;
    padding: 16px 18px;
    position: relative;
    overflow: hidden;
    transition: background 0.3s;
}

.spotlight-weather-widget + .spotlight-weather-widget {
    margin-top: 8px;
}

.spotlight-weather-widget::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px;
    opacity: 0.07;
    transition: opacity 0.3s;
    pointer-events: none;
}

.spotlight-weather--clear { background: rgba(255, 149, 0, 0.08); }
.spotlight-weather--clear::before { background: linear-gradient(135deg, #FF9500, #FF6B00); opacity: 0.06; }
.spotlight-weather--clear .spotlight-weather-condition-icon { color: #FF9500; }
.spotlight-weather--clear .spotlight-weather-temp { color: #E08600; }

.spotlight-weather--cloudy { background: rgba(0, 122, 255, 0.06); }
.spotlight-weather--cloudy::before { background: linear-gradient(135deg, #007AFF, #5856D6); opacity: 0.04; }
.spotlight-weather--cloudy .spotlight-weather-condition-icon { color: #5A9FD6; }
.spotlight-weather--cloudy .spotlight-weather-temp { color: var(--panel-text-primary); }

.spotlight-weather--overcast { background: rgba(142, 142, 147, 0.08); }
.spotlight-weather--overcast::before { background: linear-gradient(135deg, #8E8E93, #636366); opacity: 0.05; }
.spotlight-weather--overcast .spotlight-weather-condition-icon { color: #8E8E93; }
.spotlight-weather--overcast .spotlight-weather-temp { color: var(--panel-text-primary); }

.spotlight-weather--rain { background: rgba(0, 122, 255, 0.08); }
.spotlight-weather--rain::before { background: linear-gradient(135deg, #007AFF, #32ADE6); opacity: 0.06; }
.spotlight-weather--rain .spotlight-weather-condition-icon { color: #007AFF; }
.spotlight-weather--rain .spotlight-weather-temp { color: var(--panel-text-primary); }

.spotlight-weather--snow { background: rgba(88, 86, 214, 0.06); }
.spotlight-weather--snow::before { background: linear-gradient(135deg, #5856D6, #007AFF); opacity: 0.05; }
.spotlight-weather--snow .spotlight-weather-condition-icon { color: #7B9FCC; }
.spotlight-weather--snow .spotlight-weather-temp { color: var(--panel-text-primary); }

.spotlight-weather--storm { background: rgba(175, 82, 222, 0.08); }
.spotlight-weather--storm::before { background: linear-gradient(135deg, #AF52DE, #5856D6); opacity: 0.06; }
.spotlight-weather--storm .spotlight-weather-condition-icon { color: #AF52DE; }
.spotlight-weather--storm .spotlight-weather-temp { color: var(--panel-text-primary); }

.spotlight-weather--fog { background: rgba(142, 142, 147, 0.06); }
.spotlight-weather--fog::before { background: linear-gradient(135deg, #8E8E93, #AEAEB2); opacity: 0.04; }
.spotlight-weather--fog .spotlight-weather-condition-icon { color: #AEAEB2; }
.spotlight-weather--fog .spotlight-weather-temp { color: var(--panel-text-primary); }

.spotlight-weather--night { background: rgba(30, 27, 75, 0.1); }
.spotlight-weather--night::before { background: linear-gradient(135deg, #1E1B4B, #3730A3); opacity: 0.06; }
.spotlight-weather--night .spotlight-weather-condition-icon { color: #A5B4FC; }
.spotlight-weather--night .spotlight-weather-temp { color: #818CF8; }

.spotlight-weather--pending { background: rgba(142, 142, 147, 0.06); }

html.dark .spotlight-weather--clear { background: rgba(255, 159, 10, 0.1); }
html.dark .spotlight-weather--clear .spotlight-weather-condition-icon { color: #FFB340; }
html.dark .spotlight-weather--clear .spotlight-weather-temp { color: #FFB340; }
html.dark .spotlight-weather--cloudy { background: rgba(10, 132, 255, 0.1); }
html.dark .spotlight-weather--cloudy .spotlight-weather-condition-icon { color: #6CB4EE; }
html.dark .spotlight-weather--overcast { background: rgba(142, 142, 147, 0.12); }
html.dark .spotlight-weather--overcast .spotlight-weather-condition-icon { color: #AEAEB2; }
html.dark .spotlight-weather--rain { background: rgba(10, 132, 255, 0.12); }
html.dark .spotlight-weather--rain .spotlight-weather-condition-icon { color: #0A84FF; }
html.dark .spotlight-weather--snow { background: rgba(94, 92, 230, 0.1); }
html.dark .spotlight-weather--snow .spotlight-weather-condition-icon { color: #A0B8D8; }
html.dark .spotlight-weather--storm { background: rgba(191, 90, 242, 0.12); }
html.dark .spotlight-weather--storm .spotlight-weather-condition-icon { color: #BF5AF2; }
html.dark .spotlight-weather--fog { background: rgba(142, 142, 147, 0.1); }
html.dark .spotlight-weather--fog .spotlight-weather-condition-icon { color: #C7C7CC; }
html.dark .spotlight-weather--night { background: rgba(55, 48, 163, 0.15); }
html.dark .spotlight-weather--night .spotlight-weather-condition-icon { color: #C4B5FD; }
html.dark .spotlight-weather--night .spotlight-weather-temp { color: #A5B4FC; }
html.dark .spotlight-weather--pending { background: rgba(142, 142, 147, 0.1); }

html.midnight .spotlight-weather--clear { background: rgba(255, 159, 10, 0.1); }
html.midnight .spotlight-weather--clear .spotlight-weather-condition-icon { color: #FFB340; }
html.midnight .spotlight-weather--clear .spotlight-weather-temp { color: #FFB340; }
html.midnight .spotlight-weather--cloudy { background: rgba(100, 160, 220, 0.08); }
html.midnight .spotlight-weather--rain { background: rgba(10, 132, 255, 0.1); }
html.midnight .spotlight-weather--snow { background: rgba(160, 184, 216, 0.08); }
html.midnight .spotlight-weather--storm { background: rgba(191, 90, 242, 0.1); }
html.midnight .spotlight-weather--overcast { background: rgba(142, 142, 147, 0.1); }
html.midnight .spotlight-weather--fog { background: rgba(142, 142, 147, 0.08); }
html.midnight .spotlight-weather--night { background: rgba(55, 48, 163, 0.12); }
html.midnight .spotlight-weather--night .spotlight-weather-condition-icon { color: #C4B5FD; }
html.midnight .spotlight-weather--night .spotlight-weather-temp { color: #A5B4FC; }
html.midnight .spotlight-weather--pending { background: rgba(142, 142, 147, 0.08); }

html.cream .spotlight-weather--clear { background: rgba(200, 130, 20, 0.08); }
html.cream .spotlight-weather--clear .spotlight-weather-condition-icon { color: #C88214; }
html.cream .spotlight-weather--clear .spotlight-weather-temp { color: #C88214; }
html.cream .spotlight-weather--cloudy { background: rgba(5, 150, 105, 0.05); }
html.cream .spotlight-weather--rain { background: rgba(5, 120, 180, 0.06); }
html.cream .spotlight-weather--snow { background: rgba(88, 86, 214, 0.05); }
html.cream .spotlight-weather--storm { background: rgba(120, 60, 180, 0.06); }
html.cream .spotlight-weather--overcast { background: rgba(120, 120, 128, 0.06); }
html.cream .spotlight-weather--fog { background: rgba(120, 120, 128, 0.05); }
html.cream .spotlight-weather--night { background: rgba(30, 27, 75, 0.06); }
html.cream .spotlight-weather--night .spotlight-weather-condition-icon { color: #6366F1; }
html.cream .spotlight-weather--night .spotlight-weather-temp { color: #6366F1; }
html.cream .spotlight-weather--pending { background: rgba(120, 120, 128, 0.05); }

.spotlight-weather-icon-area {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
}

.spotlight-weather-condition-icon {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.spotlight-weather-condition-icon svg {
    width: 100%;
    height: 100%;
}

.spotlight-weather-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    z-index: 1;
}

.spotlight-weather-top {
    display: flex;
    align-items: center;
    gap: 12px;
}

.spotlight-weather-temp-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
    min-width: 52px;
}

.spotlight-weather-temp {
    font-size: 28px;
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.5px;
}

.spotlight-weather-feels {
    font-size: 10px;
    color: var(--panel-text-secondary);
    white-space: nowrap;
    margin-top: 1px;
}

.spotlight-weather-info {
    flex: 1;
    min-width: 0;
}

.spotlight-weather-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--panel-text-primary);
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    position: relative;
    z-index: 1;
}

.spotlight-weather-condition {
    font-size: 12px;
    color: var(--panel-text-secondary);
    margin-top: 1px;
}

.spotlight-weather-pending {
    font-size: 12px;
    color: var(--panel-text-secondary);
    font-style: italic;
}

.spotlight-weather-details {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.spotlight-weather-detail {
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    color: var(--panel-text-secondary);
    white-space: nowrap;
}

.spotlight-weather-detail svg {
    opacity: 0.55;
    flex-shrink: 0;
}

.spotlight-weather-detail-sep {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: var(--panel-text-secondary);
    opacity: 0.3;
    flex-shrink: 0;
}

.spotlight-weather-hourly {
    display: flex;
    gap: 0;
    margin-top: 6px;
    padding: 8px 4px;
    border-radius: 14px;
    background: rgba(0, 0, 0, 0.04);
    backdrop-filter: blur(40px) saturate(1.8);
    -webkit-backdrop-filter: blur(40px) saturate(1.8);
    box-shadow: inset 0 0.5px 0 rgba(255, 255, 255, 0.7), inset 0 -0.5px 0 rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.06);
}

.spotlight-weather-hour {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 2px 0;
}

.spotlight-weather-hour-time {
    font-size: 10px;
    color: var(--panel-text-secondary);
    opacity: 0.7;
}

.spotlight-weather-hour-icon {
    width: 20px;
    height: 20px;
    color: var(--panel-text-primary);
    opacity: 0.85;
}

.spotlight-weather-hour-icon svg {
    width: 100%;
    height: 100%;
}

.spotlight-weather-hour-temp {
    font-size: 11px;
    font-weight: 600;
    color: var(--panel-text-primary);
}

html.dark .spotlight-weather-hourly,
html.midnight .spotlight-weather-hourly {
    background: rgba(255, 255, 255, 0.05);
    box-shadow: inset 0 0.5px 0 rgba(255, 255, 255, 0.1), 0 1px 4px rgba(0, 0, 0, 0.15);
}

html.cream .spotlight-weather-hourly {
    background: rgba(0, 0, 0, 0.03);
    box-shadow: inset 0 0.5px 0 rgba(255, 255, 255, 0.3), 0 1px 3px rgba(0, 0, 0, 0.03);
}

.spotlight-weather-tomorrow {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--panel-text-secondary);
    margin-top: 4px;
    padding: 6px 10px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.04);
    backdrop-filter: blur(40px) saturate(1.8);
    -webkit-backdrop-filter: blur(40px) saturate(1.8);
    box-shadow: inset 0 0.5px 0 rgba(255, 255, 255, 0.7), inset 0 -0.5px 0 rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.06);
}

html.dark .spotlight-weather-tomorrow,
html.midnight .spotlight-weather-tomorrow {
    background: rgba(255, 255, 255, 0.05);
    box-shadow: inset 0 0.5px 0 rgba(255, 255, 255, 0.1), 0 1px 4px rgba(0, 0, 0, 0.15);
}

html.cream .spotlight-weather-tomorrow {
    background: rgba(0, 0, 0, 0.03);
    box-shadow: inset 0 0.5px 0 rgba(255, 255, 255, 0.3), 0 1px 3px rgba(0, 0, 0, 0.03);
}

.spotlight-weather-tomorrow-icon {
    width: 18px;
    height: 18px;
    color: var(--panel-text-primary);
    opacity: 0.85;
}

.spotlight-weather-tomorrow-icon svg {
    width: 100%;
    height: 100%;
}

.spotlight-weather-tomorrow-temps {
    font-weight: 600;
    color: var(--panel-text-primary);
}

.spotlight-weather-tomorrow-precip {
    margin-left: auto;
}

.spotlight-weather-icon--loading {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: linear-gradient(135deg, #8E8E93, #636366);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.sw-fx {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
    border-radius: 12px;
    z-index: 0;
}

.sw-fx--sunny span {
    position: absolute;
    transform-origin: right center;
    background: linear-gradient(90deg, transparent, rgba(255, 180, 50, 0.35), transparent);
    animation: swSunRay ease-in-out infinite;
}

.sw-fx--sunny span:nth-child(1) { width: 110%; height: 18px; top: 8%; right: 0; border-radius: 8px; transform: rotate(-12deg); animation-duration: 4s; animation-delay: 0s; }
.sw-fx--sunny span:nth-child(2) { width: 90%; height: 12px; top: 45%; right: 0; border-radius: 6px; transform: rotate(-6deg); animation-duration: 5s; animation-delay: 1.8s; }
.sw-fx--sunny span:nth-child(3) { width: 100%; height: 14px; top: 75%; right: 0; border-radius: 7px; transform: rotate(-9deg); animation-duration: 4.5s; animation-delay: 0.6s; }

@keyframes swSunRay {
    0%, 100% { opacity: 0; transform: translateX(30%) rotate(-12deg); }
    30%      { opacity: 0.6; }
    50%      { opacity: 0.8; }
    70%      { opacity: 0.5; }
    100%     { opacity: 0; transform: translateX(-80%) rotate(-12deg); }
}

html.dark .sw-fx--sunny span { background: linear-gradient(90deg, transparent, rgba(255, 200, 80, 0.25), transparent); }

.sw-fx--cloudy span {
    position: absolute;
    border-radius: 10px;
    background: rgb(120, 160, 210);
    --cloud-peak: 0.2;
    animation: swCloudMove linear infinite;
}

.sw-fx--cloudy span::before,
.sw-fx--cloudy span::after {
    content: '';
    position: absolute;
    background: inherit;
    border-radius: 50%;
}

.sw-fx--cloudy span:nth-child(1) { width: 60px; height: 18px; top: 10px; left: -70px; animation-duration: 10s; animation-delay: 0s; }
.sw-fx--cloudy span:nth-child(1)::before { width: 28px; height: 28px; top: -16px; left: 8px; }
.sw-fx--cloudy span:nth-child(1)::after  { width: 20px; height: 20px; top: -10px; right: 8px; }

.sw-fx--cloudy span:nth-child(2) { width: 46px; height: 14px; top: 42px; left: -54px; animation-duration: 14s; animation-delay: 3s; }
.sw-fx--cloudy span:nth-child(2)::before { width: 22px; height: 22px; top: -12px; left: 6px; }
.sw-fx--cloudy span:nth-child(2)::after  { width: 16px; height: 16px; top: -8px; right: 6px; }

.sw-fx--cloudy span:nth-child(3) { width: 52px; height: 16px; top: 24px; left: -62px; animation-duration: 8s; animation-delay: 6s; }
.sw-fx--cloudy span:nth-child(3)::before { width: 26px; height: 26px; top: -14px; left: 7px; }
.sw-fx--cloudy span:nth-child(3)::after  { width: 18px; height: 18px; top: -9px; right: 7px; }

@keyframes swCloudMove {
    0%   { transform: translateX(0); opacity: 0; }
    5%   { opacity: var(--cloud-peak, 0.2); }
    90%  { opacity: var(--cloud-peak, 0.2); }
    100% { transform: translateX(700px); opacity: 0; }
}

html.dark .sw-fx--cloudy span { background: rgb(140, 180, 230); --cloud-peak: 0.18; }

.sw-fx--overcast span {
    position: absolute;
    border-radius: 12px;
    background: rgb(130, 130, 145);
    --cloud-peak: 0.22;
    animation: swCloudMove linear infinite;
}

.sw-fx--overcast span::before,
.sw-fx--overcast span::after {
    content: '';
    position: absolute;
    background: inherit;
    border-radius: 50%;
}

.sw-fx--overcast span:nth-child(1) { width: 68px; height: 20px; top: 6px; left: -78px; animation-duration: 11s; animation-delay: 0s; }
.sw-fx--overcast span:nth-child(1)::before { width: 32px; height: 32px; top: -18px; left: 10px; }
.sw-fx--overcast span:nth-child(1)::after  { width: 24px; height: 24px; top: -12px; right: 8px; }

.sw-fx--overcast span:nth-child(2) { width: 54px; height: 16px; top: 40px; left: -64px; animation-duration: 15s; animation-delay: 4s; }
.sw-fx--overcast span:nth-child(2)::before { width: 26px; height: 26px; top: -14px; left: 7px; }
.sw-fx--overcast span:nth-child(2)::after  { width: 18px; height: 18px; top: -9px; right: 7px; }

.sw-fx--overcast span:nth-child(3) { width: 62px; height: 18px; top: 22px; left: -72px; animation-duration: 9s; animation-delay: 7s; }
.sw-fx--overcast span:nth-child(3)::before { width: 30px; height: 30px; top: -16px; left: 8px; }
.sw-fx--overcast span:nth-child(3)::after  { width: 22px; height: 22px; top: -11px; right: 8px; }

html.dark .sw-fx--overcast span { background: rgb(160, 160, 175); --cloud-peak: 0.2; }

.sw-fx--rain span,
.sw-fx--drizzle span {
    position: absolute;
    top: -20px;
    width: 1.5px;
    background: linear-gradient(to bottom, transparent, rgba(0, 122, 255, 0.35));
    border-radius: 0 0 2px 2px;
    animation: swRainFall linear infinite;
}

.sw-fx--rain span    { height: 18px; }
.sw-fx--drizzle span { height: 12px; background: linear-gradient(to bottom, transparent, rgba(0, 122, 255, 0.2)); }

.sw-fx--rain span:nth-child(1)  { left:  5%; animation-duration: 0.7s; animation-delay: 0.0s; }
.sw-fx--rain span:nth-child(2)  { left: 15%; animation-duration: 0.6s; animation-delay: 0.15s; }
.sw-fx--rain span:nth-child(3)  { left: 25%; animation-duration: 0.8s; animation-delay: 0.3s; }
.sw-fx--rain span:nth-child(4)  { left: 35%; animation-duration: 0.65s; animation-delay: 0.1s; }
.sw-fx--rain span:nth-child(5)  { left: 45%; animation-duration: 0.75s; animation-delay: 0.4s; }
.sw-fx--rain span:nth-child(6)  { left: 55%; animation-duration: 0.6s; animation-delay: 0.25s; }
.sw-fx--rain span:nth-child(7)  { left: 65%; animation-duration: 0.7s; animation-delay: 0.05s; }
.sw-fx--rain span:nth-child(8)  { left: 75%; animation-duration: 0.8s; animation-delay: 0.35s; }
.sw-fx--rain span:nth-child(9)  { left: 85%; animation-duration: 0.65s; animation-delay: 0.2s; }
.sw-fx--rain span:nth-child(10) { left: 92%; animation-duration: 0.7s; animation-delay: 0.45s; }

.sw-fx--drizzle span:nth-child(1) { left:  8%; animation-duration: 1.2s; animation-delay: 0.0s; }
.sw-fx--drizzle span:nth-child(2) { left: 22%; animation-duration: 1.4s; animation-delay: 0.3s; }
.sw-fx--drizzle span:nth-child(3) { left: 40%; animation-duration: 1.1s; animation-delay: 0.6s; }
.sw-fx--drizzle span:nth-child(4) { left: 58%; animation-duration: 1.3s; animation-delay: 0.15s; }
.sw-fx--drizzle span:nth-child(5) { left: 75%; animation-duration: 1.2s; animation-delay: 0.5s; }
.sw-fx--drizzle span:nth-child(6) { left: 90%; animation-duration: 1.4s; animation-delay: 0.8s; }

@keyframes swRainFall {
    0%   { transform: translateY(-20px); opacity: 0; }
    10%  { opacity: 1; }
    90%  { opacity: 1; }
    100% { transform: translateY(100px); opacity: 0; }
}

html.dark .sw-fx--rain span    { background: linear-gradient(to bottom, transparent, rgba(10, 132, 255, 0.4)); }
html.dark .sw-fx--drizzle span { background: linear-gradient(to bottom, transparent, rgba(10, 132, 255, 0.25)); }

.sw-fx--snow span {
    position: absolute;
    top: -8px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: rgba(88, 86, 214, 0.25);
    animation: swSnowFall linear infinite;
}

.sw-fx--snow span:nth-child(1) { left:  6%; width: 4px; height: 4px; animation-duration: 2.5s; animation-delay: 0.0s; }
.sw-fx--snow span:nth-child(2) { left: 18%; width: 6px; height: 6px; animation-duration: 3.0s; animation-delay: 0.4s; }
.sw-fx--snow span:nth-child(3) { left: 30%; width: 3px; height: 3px; animation-duration: 2.8s; animation-delay: 0.8s; }
.sw-fx--snow span:nth-child(4) { left: 42%; width: 5px; height: 5px; animation-duration: 2.6s; animation-delay: 0.2s; }
.sw-fx--snow span:nth-child(5) { left: 56%; width: 4px; height: 4px; animation-duration: 3.2s; animation-delay: 1.0s; }
.sw-fx--snow span:nth-child(6) { left: 68%; width: 6px; height: 6px; animation-duration: 2.7s; animation-delay: 0.6s; }
.sw-fx--snow span:nth-child(7) { left: 80%; width: 3px; height: 3px; animation-duration: 2.9s; animation-delay: 1.2s; }
.sw-fx--snow span:nth-child(8) { left: 92%; width: 5px; height: 5px; animation-duration: 2.5s; animation-delay: 0.3s; }

@keyframes swSnowFall {
    0%   { transform: translateY(-8px) translateX(0); opacity: 0; }
    10%  { opacity: 0.8; }
    50%  { transform: translateY(45px) translateX(8px); }
    90%  { opacity: 0.6; }
    100% { transform: translateY(100px) translateX(-4px); opacity: 0; }
}

html.dark .sw-fx--snow span { background: rgba(160, 184, 216, 0.3); }

.sw-fx--storm span {
    position: absolute;
    top: -20px;
    width: 1.5px;
    height: 18px;
    background: linear-gradient(to bottom, transparent, rgba(175, 82, 222, 0.35));
    border-radius: 0 0 2px 2px;
    animation: swRainFall linear infinite;
}

.sw-fx--storm span:nth-child(1)  { left:  4%; animation-duration: 0.55s; animation-delay: 0.0s; }
.sw-fx--storm span:nth-child(2)  { left: 12%; animation-duration: 0.5s;  animation-delay: 0.1s; }
.sw-fx--storm span:nth-child(3)  { left: 22%; animation-duration: 0.6s;  animation-delay: 0.25s; }
.sw-fx--storm span:nth-child(4)  { left: 30%; animation-duration: 0.5s;  animation-delay: 0.35s; }
.sw-fx--storm span:nth-child(5)  { left: 40%; animation-duration: 0.55s; animation-delay: 0.05s; }
.sw-fx--storm span:nth-child(6)  { left: 50%; animation-duration: 0.6s;  animation-delay: 0.2s; }
.sw-fx--storm span:nth-child(7)  { left: 60%; animation-duration: 0.5s;  animation-delay: 0.4s; }
.sw-fx--storm span:nth-child(8)  { left: 68%; animation-duration: 0.55s; animation-delay: 0.15s; }
.sw-fx--storm span:nth-child(9)  { left: 78%; animation-duration: 0.6s;  animation-delay: 0.3s; }
.sw-fx--storm span:nth-child(10) { left: 86%; animation-duration: 0.5s;  animation-delay: 0.08s; }
.sw-fx--storm span:nth-child(11) { left: 93%; animation-duration: 0.55s; animation-delay: 0.22s; }

.sw-fx--storm span:nth-child(12) {
    top: 0; left: 0; width: 100%; height: 100%;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.12);
    animation: swLightning 4s ease-in-out infinite;
}

@keyframes swLightning {
    0%, 100% { opacity: 0; }
    4%       { opacity: 1; }
    6%       { opacity: 0; }
    8%       { opacity: 0.7; }
    9%       { opacity: 0; }
    50%      { opacity: 0; }
    52%      { opacity: 0.5; }
    53%      { opacity: 0; }
}

html.dark .sw-fx--storm span:not(:nth-child(12)) { background: linear-gradient(to bottom, transparent, rgba(191, 90, 242, 0.4)); }
html.dark .sw-fx--storm span:nth-child(12)       { background: rgba(191, 90, 242, 0.08); }

.sw-fx--fog span {
    position: absolute;
    left: -20%;
    width: 140%;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg, transparent 0%, rgba(142, 142, 147, 0.15) 30%, rgba(142, 142, 147, 0.2) 50%, rgba(142, 142, 147, 0.15) 70%, transparent 100%);
    animation: swFogDrift linear infinite;
}

.sw-fx--fog span:nth-child(1) { top: 20%; height: 3px; animation-duration: 8s;  animation-delay: 0s; }
.sw-fx--fog span:nth-child(2) { top: 40%; height: 2px; animation-duration: 12s; animation-delay: 2s; animation-direction: reverse; }
.sw-fx--fog span:nth-child(3) { top: 60%; height: 3px; animation-duration: 10s; animation-delay: 4s; }
.sw-fx--fog span:nth-child(4) { top: 80%; height: 2px; animation-duration: 9s;  animation-delay: 1s; animation-direction: reverse; }

@keyframes swFogDrift {
    0%   { transform: translateX(-30%); opacity: 0; }
    20%  { opacity: 1; }
    80%  { opacity: 1; }
    100% { transform: translateX(30%); opacity: 0; }
}

html.dark .sw-fx--fog span { background: linear-gradient(90deg, transparent 0%, rgba(199, 199, 204, 0.1) 30%, rgba(199, 199, 204, 0.15) 50%, rgba(199, 199, 204, 0.1) 70%, transparent 100%); }

.sw-fx--night span {
    position: absolute;
    border-radius: 50%;
    background: rgba(165, 180, 252, 0.5);
    animation: swStarTwinkle ease-in-out infinite;
}

.sw-fx--night span:nth-child(1) { width: 3px; height: 3px; top: 15%; left: 12%; animation-duration: 3s; animation-delay: 0s; }
.sw-fx--night span:nth-child(2) { width: 2px; height: 2px; top: 35%; left: 70%; animation-duration: 4s; animation-delay: 1s; }
.sw-fx--night span:nth-child(3) { width: 3px; height: 3px; top: 60%; left: 40%; animation-duration: 3.5s; animation-delay: 0.5s; }
.sw-fx--night span:nth-child(4) { width: 2px; height: 2px; top: 22%; left: 85%; animation-duration: 4.5s; animation-delay: 1.5s; }
.sw-fx--night span:nth-child(5) { width: 2px; height: 2px; top: 72%; left: 20%; animation-duration: 3.8s; animation-delay: 2s; }

@keyframes swStarTwinkle {
    0%, 100% { opacity: 0.15; transform: scale(1); }
    50%      { opacity: 0.8; transform: scale(1.4); }
}

html.dark .sw-fx--night span { background: rgba(196, 181, 253, 0.6); }

@media (prefers-reduced-motion: reduce) {
    .sw-fx span { animation: none !important; opacity: 0.15; }
    .sw-fx--storm span:nth-child(12) { opacity: 0 !important; }
}

.spotlight-app.panel-app--compact .spotlight-weather-widget {
    padding: 12px 14px;
    gap: 10px;
}

.spotlight-app.panel-app--compact .spotlight-weather-icon-area {
    width: 40px;
    height: 40px;
}

.spotlight-app.panel-app--compact .spotlight-weather-condition-icon {
    width: 36px;
    height: 36px;
}

.spotlight-app.panel-app--compact .spotlight-weather-temp {
    font-size: 24px;
}

.spotlight-app.panel-app--compact .spotlight-weather-temp-block {
    min-width: 44px;
}

.spotlight-app.panel-app--compact .spotlight-weather-title {
    font-size: 13px;
}

.spotlight-app.panel-app--compact .spotlight-weather-hourly,
.spotlight-app.panel-app--compact .spotlight-weather-tomorrow {
    display: none;
}
</style>
